import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Typography, Button, Space } from 'antd';
import {
  LeftOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  RobotOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import {
  startCampaignPreview,
  sendCampaignPreviewMessage,
  resetCampaignPreview,
  renderCampaignPreviewMessage,
} from '../../reducers/campaign/resources';
import './PhonePreviewSimulator.css';

const { Text } = Typography;

const INPUT_HINTS = {
  text: 'Type a reply...',
  email: 'Enter your email...',
  number: 'Enter a number...',
  date: 'YYYY-MM-DD',
  'zip-code': 'Enter a zip code...',
  'keyword-fork': 'Type a keyword...',
  options: 'Pick a number...',
};

const HTML_INPUT_TYPE = {
  email: 'email',
  number: 'number',
  date: 'date',
};

function unwrap(res) {
  return res?.data?.data || res?.data || res;
}

export default function LivePhonePreview({
  modulegroupId,
  moduleResponseOverrides,
  availabilitySettings,
  noSlotsMessage,
  timezone,
  onTerminalChange,
  resetKey,
  customMessage,
  notificationUsers,
  messageType,
  contactId,
  simulateNoSlots,
  schedulingUserId,
  schedulingUserIds,
}) {
  // ── Custom-message mode: render the broadcast bubble + "Mark as reviewed" gate ──
  if (messageType !== 'auto' || !modulegroupId) {
    return (
      <CustomMessagePreview
        message={customMessage}
        notificationUsers={notificationUsers}
        contactId={contactId}
        onTerminalChange={onTerminalChange}
        resetKey={resetKey}
      />
    );
  }

  return (
    <AgentLivePreview
      modulegroupId={modulegroupId}
      moduleResponseOverrides={moduleResponseOverrides}
      availabilitySettings={availabilitySettings}
      noSlotsMessage={noSlotsMessage}
      timezone={timezone}
      contactId={contactId}
      onTerminalChange={onTerminalChange}
      resetKey={resetKey}
      simulateNoSlots={simulateNoSlots}
      schedulingUserId={schedulingUserId}
      schedulingUserIds={schedulingUserIds}
    />
  );
}

// ─────────────────────────────────────────────────────────
// Custom-message preview (single broadcast bubble)
// ─────────────────────────────────────────────────────────
function CustomMessagePreview({ message, notificationUsers, contactId, onTerminalChange, resetKey }) {
  const [reviewed, setReviewed] = useState(false);
  const [rendered, setRendered] = useState(message || '');
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    setReviewed(false);
    onTerminalChange(false);
  }, [resetKey]);

  // Render template against the selected contact via the backend. Reuses the
  // exact same merge logic the agent-flow path uses, so {{firstname}},
  // {{recruiter_name}}, {{brandname}}, etc. resolve identically. When no
  // contactId is available we still call the endpoint — it falls back to the
  // synthetic Preview User and strips unknown tokens so the author can see
  // what unresolved placeholders look like.
  useEffect(() => {
    if (!message) {
      setRendered('');
      return undefined;
    }
    if (!message.includes('{{')) {
      // Fast path: no mail-merge tokens, skip the round-trip.
      setRendered(message);
      return undefined;
    }
    let cancelled = false;
    setRendering(true);
    renderCampaignPreviewMessage({ message, contact_id: contactId || undefined })
      .then((res) => {
        if (cancelled) return;
        const body = unwrap(res);
        setRendered(body?.rendered ?? message);
      })
      .catch(() => {
        if (cancelled) return;
        setRendered(message);
      })
      .finally(() => { if (!cancelled) setRendering(false); });
    return () => { cancelled = true; };
  }, [message, contactId]);

  const markReviewed = () => {
    setReviewed(true);
    onTerminalChange(true);
  };

  return (
    <div className="phone-sim-container">
      <div className="phone-sim-header">
        <div className="phone-sim-header-left">
          <LeftOutlined style={{ color: '#1890ff', fontSize: 12 }} />
          <span>Broadcast preview</span>
        </div>
      </div>

      <div className="phone-sim-chat">
        {message ? (
          <div className="phone-sim-bubble bot" style={rendering ? { opacity: 0.6 } : undefined}>
            {rendered}
          </div>
        ) : (
          <div className="phone-sim-bubble bot" style={{ color: '#8c8c8c' }}>
            (no message — write your broadcast)
          </div>
        )}

        <div style={{ marginTop: 'auto', padding: 12, textAlign: 'center' }}>
          {reviewed ? (
            <Text style={{ color: '#389e0d', fontSize: 12 }}>
              <CheckCircleOutlined /> Marked as reviewed
            </Text>
          ) : (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={markReviewed}
              disabled={!message || !message.trim()}
            >
              Mark as reviewed
            </Button>
          )}
        </div>
      </div>

      <div className="phone-sim-footer">
        <Space>
          <PlusOutlined style={{ color: '#8c8c8c' }} />
          <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
            {(notificationUsers || []).length
              ? `Replies notify ${notificationUsers.length} user${notificationUsers.length === 1 ? '' : 's'}`
              : 'Text Message'}
          </span>
        </Space>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Agent live preview — talks to the backend preview engine
// ─────────────────────────────────────────────────────────
function AgentLivePreview({
  modulegroupId, moduleResponseOverrides, availabilitySettings, noSlotsMessage, timezone,
  contactId, onTerminalChange, resetKey, simulateNoSlots, schedulingUserId, schedulingUserIds,
}) {
  const [previewId, setPreviewId] = useState(null);
  const [moduleGroupName, setModuleGroupName] = useState('');
  const [messages, setMessages] = useState([]);
  const [awaiting, setAwaiting] = useState(null);
  const [terminal, setTerminal] = useState(false);
  const [terminalReason, setTerminalReason] = useState(null);
  const [sessionTerminalReached, setSessionTerminalReached] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const chatRef = useRef(null);
  const previewIdRef = useRef(null);

  // Sticky gate: once terminal is reached in this preview session, it stays
  // satisfied even if the user keeps walking through loopback branches.
  useEffect(() => {
    if (terminal && !sessionTerminalReached) {
      setSessionTerminalReached(true);
      onTerminalChange(true);
    }
  }, [terminal, sessionTerminalReached]);

  // Auto-scroll
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Start (and restart on resetKey) the preview session
  useEffect(() => {
    let cancelled = false;

    async function startPreview() {
      // Tear down any prior preview session before starting a fresh one
      const prior = previewIdRef.current;
      if (prior) {
        try {
          await resetCampaignPreview(prior);
        } catch {
          // Ignore — server may have already expired the session
        }
        previewIdRef.current = null;
      }
      if (!modulegroupId) return;

      setLoading(true);
      setError(null);
      setMessages([]);
      setAwaiting(null);
      setTerminal(false);
      setSessionTerminalReached(false);
      onTerminalChange(false);
      setInputValue('');

      try {
        const res = await startCampaignPreview({
          modulegroup_id: modulegroupId,
          module_response_overrides: moduleResponseOverrides && Object.keys(moduleResponseOverrides).length
            ? moduleResponseOverrides
            : undefined,
          availability_settings: availabilitySettings || undefined,
          no_slots_message: noSlotsMessage || undefined,
          timezone: timezone || undefined,
          contact_id: contactId || undefined,
          simulate_no_slots: simulateNoSlots || undefined,
          scheduling_user_id: schedulingUserId || undefined,
          scheduling_user_ids: (schedulingUserIds && schedulingUserIds.length) ? schedulingUserIds : undefined,
        });
        if (cancelled) return;
        const body = unwrap(res);
        setPreviewId(body.previewId);
        previewIdRef.current = body.previewId;
        setModuleGroupName(body.moduleGroupName || '');
        setMessages(body.messages || []);
        setAwaiting(body.awaiting || null);
        setTerminal(!!body.terminal);
        setTerminalReason(body.terminalReason || null);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to start preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    startPreview();

    return () => {
      cancelled = true;
    };
    // contactId is included so the preview re-starts once the async
    // loadPreviewAudience finishes — otherwise the first modal open renders
    // mailmerges blank because the session was created against the synthetic
    // Preview User. The contact cycler also changes contactId, but cycling
    // already bumps resetKey, so the dep doesn't cause an extra restart there.
  }, [modulegroupId, resetKey, contactId, schedulingUserId, JSON.stringify(schedulingUserIds || [])]);

  // Cleanup when component unmounts
  useEffect(() => () => {
    if (previewIdRef.current) {
      resetCampaignPreview(previewIdRef.current).catch(() => {});
    }
  }, []);

  const sendReply = useCallback(async (replyBody, opts = {}) => {
    if (!previewId) return;
    const optimisticUser = opts.bookingSlot
      ? { type: 'user', text: opts.bookingSlot.title || 'Selected time slot' }
      : { type: 'user', text: replyBody };
    setMessages((prev) => [...prev, optimisticUser]);
    setSending(true);
    setInputValue('');

    try {
      const res = await sendCampaignPreviewMessage(previewId, {
        body: replyBody || '',
        bookingSlot: opts.bookingSlot || undefined,
      });
      const body = unwrap(res);
      setMessages((prev) => prev.concat(body.messages || []));
      setAwaiting(body.awaiting || null);
      setTerminal(!!body.terminal);
      setTerminalReason(body.terminalReason || null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setMessages((prev) => prev.concat([{
          type: 'system',
          text: 'Preview session expired — restarting…',
        }]));
        previewIdRef.current = null;
        setPreviewId(null);
        // Trigger restart via resetKey-style effect by clearing state
        setTimeout(() => {
          // Re-run by toggling local state: easiest is to bump a local counter
          setMessages([]);
          setAwaiting(null);
          setTerminal(false);
          // Start a new session
          startCampaignPreview({
            modulegroup_id: modulegroupId,
            module_response_overrides: moduleResponseOverrides || undefined,
            availability_settings: availabilitySettings || undefined,
            no_slots_message: noSlotsMessage || undefined,
            timezone: timezone || undefined,
            contact_id: contactId || undefined,
            scheduling_user_id: schedulingUserId || undefined,
            scheduling_user_ids: (schedulingUserIds && schedulingUserIds.length) ? schedulingUserIds : undefined,
          }).then((r) => {
            const b = unwrap(r);
            setPreviewId(b.previewId);
            previewIdRef.current = b.previewId;
            setMessages(b.messages || []);
            setAwaiting(b.awaiting || null);
            setTerminal(!!b.terminal);
            setTerminalReason(b.terminalReason || null);
          }).catch(() => {});
        }, 200);
      } else {
        setMessages((prev) => prev.concat([{
          type: 'error',
          text: err?.message || 'Failed to send reply',
        }]));
      }
    } finally {
      setSending(false);
    }
  }, [previewId, modulegroupId, moduleResponseOverrides, availabilitySettings, noSlotsMessage, timezone]);

  const handleSubmitText = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    sendReply(trimmed);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmitText();
  };

  const inputType = HTML_INPUT_TYPE[awaiting?.type] || 'text';
  let placeholder = INPUT_HINTS[awaiting?.type] || 'Type a reply...';
  if (awaiting?.type === 'time-slots') placeholder = 'Reply 1, 2, 3, MORE, or try an invalid input...';
  // time-slots shows both chips AND a text input — so the user can try invalid
  // replies and verify the agent's error_response.
  const isTextInput = awaiting && !['options', 'keyword-fork', 'schedule', 'gpt'].includes(awaiting.type);
  const quickReplies = awaiting && ['options', 'keyword-fork', 'time-slots'].includes(awaiting.type)
    ? (awaiting.options || [])
    : [];

  if (loading && !messages.length) {
    return (
      <div className="phone-sim-container">
        <div className="phone-sim-header">
          <div className="phone-sim-header-left">
            <LeftOutlined style={{ color: '#1890ff', fontSize: 12 }} />
            <span>Loading preview…</span>
          </div>
        </div>
        <div className="phone-sim-chat" style={{ alignItems: 'center', justifyContent: 'center' }}>
          <div className="phone-sim-typing">
            <div className="phone-sim-typing-dot" />
            <div className="phone-sim-typing-dot" />
            <div className="phone-sim-typing-dot" />
          </div>
        </div>
        <div className="phone-sim-footer" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="phone-sim-container">
        <div className="phone-sim-header">
          <div className="phone-sim-header-left">
            <span style={{ color: '#cf1322' }}>Preview error</span>
          </div>
        </div>
        <div className="phone-sim-chat" style={{ padding: 16 }}>
          <Text type="danger" style={{ fontSize: 12 }}>{error}</Text>
        </div>
        <div className="phone-sim-footer" />
      </div>
    );
  }

  return (
    <div className="phone-sim-container">
      <div className="phone-sim-header">
        <div className="phone-sim-header-left">
          <LeftOutlined style={{ color: '#1890ff', fontSize: 12 }} />
          <RobotOutlined style={{ color: '#52c41a' }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {moduleGroupName || 'Agent'}
          </span>
        </div>
      </div>

      <div className="phone-sim-chat" ref={chatRef}>
        {messages.map((msg, idx) => (
          <React.Fragment key={idx}>
            {msg.moduleTitle && msg.type === 'bot' && (
              <div className="phone-sim-module-tag">{msg.moduleTitle}</div>
            )}
            <div className={`phone-sim-bubble ${msg.type}`}>{msg.text}</div>
          </React.Fragment>
        ))}

        {sending && (
          <div className="phone-sim-typing">
            <div className="phone-sim-typing-dot" />
            <div className="phone-sim-typing-dot" />
            <div className="phone-sim-typing-dot" />
          </div>
        )}

        {!sending && quickReplies.length > 0 && (
          <div className="phone-sim-quick-replies">
            {quickReplies.map((qr, i) => (
              <button
                key={i}
                className="phone-sim-chip"
                onClick={() => sendReply(qr.value)}
                type="button"
                title={awaiting?.type === 'time-slots' ? qr.label : undefined}
              >
                {awaiting?.type === 'time-slots' ? qr.value : qr.label}
              </button>
            ))}
            {awaiting?.type === 'time-slots' && awaiting.supportsMore && (
              <button
                className="phone-sim-chip"
                onClick={() => sendReply('MORE')}
                type="button"
                style={{ borderColor: '#1890ff', color: '#1890ff' }}
              >
                <CalendarOutlined style={{ marginRight: 4 }} /> MORE
              </button>
            )}
          </div>
        )}

        {!sending && awaiting?.type === 'time-slots' && (awaiting.options || []).length === 0 && (
          <div className="phone-sim-bubble system">
            No available time slots returned — connect your calendar or adjust availability settings.
          </div>
        )}

        {!sending && awaiting?.type === 'gpt' && (
          <div className="phone-sim-bubble system">
            GPT modules cannot be tested in preview — they will run in production.
          </div>
        )}

        {terminal && (
          <div className="phone-sim-ended">
            <CheckCircleOutlined />
            <span>
              {terminalReason === 'booking-confirmed' && 'Booking confirmed (preview)'}
              {terminalReason === 'flow-end' && 'Flow complete'}
              {terminalReason === 'talk-to-agent' && 'Handover to agent'}
              {terminalReason === 'gpt-preview-unsupported' && 'GPT module reached'}
              {terminalReason === 'no-calendar' && 'Calendar not connected — flow halted'}
              {terminalReason === 'no-slots' && 'No slots available — flow halted'}
              {terminalReason === 'loop-detected' && "You've explored a loop — preview marked complete"}
              {!terminalReason && 'Preview complete'}
            </span>
          </div>
        )}
      </div>

      {isTextInput ? (
        <div className="phone-sim-input-area">
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={sending}
            type={inputType}
          />
          <button
            className="phone-sim-send-btn"
            onClick={handleSubmitText}
            disabled={sending || !inputValue.trim()}
            type="button"
          >
            ↑
          </button>
        </div>
      ) : (
        <div className="phone-sim-footer">
          <Space>
            <PlusOutlined style={{ color: '#8c8c8c' }} />
            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
              {terminal ? 'Preview complete' : 'Waiting for reply...'}
            </span>
          </Space>
        </div>
      )}
    </div>
  );
}
